import React, { useState, useEffect } from "react";
import { Table, Button, Typography, Input, Select, AutoComplete, Space, Spin, Card } from "antd";
import { useHistory } from "react-router-dom";
import useApiCall from "../common/api-call";
import {
  listPremades,
  reloadPremades,
  targetPremades,
  listAspects,
} from "./premade-service.js";
import { useToast } from "../common/toast";
import PremadeScriptDialog from "./premade-script-dialog";

const { Option } = Select;

const PremadeManageView = () => {
  const { apiCall } = useApiCall();
  const history = useHistory();
  const [premades, setPremades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [envFilter, setEnvFilter] = useState("both");
  const [capabilitySearch, setCapabilitySearch] = useState([]);
  const [functionSearch, setFunctionSearch] = useState([]);
  const [isTargetSearch, setIsTargetSearch] = useState(false);
  const [aspects, setAspects] = useState({
    capabilities: [],
    functions: [],
    integrations: [],
  });
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [selectedPremade, setSelectedPremade] = useState(null);
  const { errorToast, successToast } = useToast();

  useEffect(() => {
    fetchData(false);
    loadAspects();
  }, []);

  const loadAspects = async () => {
    try {
      const aspectsResult = await listAspects(apiCall);
      if (aspectsResult) {
        setAspects({
          capabilities: aspectsResult.capabilities || [],
          functions: aspectsResult.functions || [],
          integrations: aspectsResult.integrations || [],
        });
      }
    } catch (error) {
      console.error("Failed to load aspects:", error);
    }
  };

  const fetchData = async (targetSearch = false) => {
    setLoading(true);
    try {
      let premadeMap = new Map();

      if (
        targetSearch &&
        (capabilitySearch.length > 0 || functionSearch.length > 0)
      ) {
        // Use target search
        const targetResult = await targetPremades(
          apiCall,
          capabilitySearch,
          functionSearch,
        );

        if (targetResult?.premades) {
          targetResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: premade.version === "prod" ? "prod" : "stg",
              version: premade.version,
            });
          });
        }
      } else {
        // Use regular list search
        const [prodResult, stgResult] = await Promise.all([
          listPremades(apiCall, "prod"),
          listPremades(apiCall, "stg"),
        ]);

        // Process prod data
        if (prodResult?.premades) {
          prodResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: "prod",
              version: premade.version,
            });
          });
        }

        // Process stg data
        if (stgResult?.premades) {
          stgResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: "stg",
              version: premade.version,
            });
          });
        }
      }

      // Convert map to array and sort by ID
      const mergedPremades = Array.from(premadeMap.values()).sort((a, b) =>
        a.id.localeCompare(b.id),
      );

      setPremades(mergedPremades);
    } catch (error) {
      console.error("Failed to fetch premades:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    try {
      const resp = await reloadPremades(apiCall);
      if (resp && !resp.error) {
        setIsTargetSearch(false);
        setCapabilitySearch([]);
        setFunctionSearch([]);
        await fetchData(false);
        await loadAspects();
      } else {
        errorToast("Error");
      }
    } catch (error) {
      errorToast("Error");
    }
  };

  const handleSearch = () => {
    setIsTargetSearch(true);
    fetchData(true);
  };

  const handleClearSearch = () => {
    setIsTargetSearch(false);
    setCapabilitySearch([]);
    setFunctionSearch([]);
    fetchData(false);
  };

  const filteredPremades = (Array.isArray(premades) ? premades : []).filter(premade => {
    // First apply search filter
    const matchesSearch =
      premade.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      premade.description?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Then apply environment filter
    if (envFilter === "both") return true;

    // Check if premade has the selected environment
    return Array.isArray(premade.versions) && premade.versions.some(version => version.env === envFilter);
  });

  const handleOpenPremade = (premadeId, env) => {
    // Find the version for the specific environment
    const premade = premades.find(p => p.id === premadeId);
    if (premade) {
      const versionInfo = premade.versions.find(v => v.env === env);
      if (versionInfo) {
        setSelectedPremade({
          premadeId: premadeId,
          version: versionInfo.version,
        });
        setScriptDialogOpen(true);
      }
    }
  };

  const getVersionButtons = (premadeId, versions) => {
    const prodVersion = versions.find(v => v.env === "prod");
    const stgVersion = versions.find(v => v.env === "stg");

    return (
      <Space>
        {prodVersion && (envFilter === "both" || envFilter === "prod") && (
          <Button
            size="small"
            type="primary"
            onClick={() => handleOpenPremade(premadeId, "prod")}
            style={{ minWidth: 60 }}
          >
            Prod
          </Button>
        )}
        {stgVersion && (envFilter === "both" || envFilter === "stg") && (
          <Button
            size="small"
            onClick={() => handleOpenPremade(premadeId, "stg")}
            style={{ minWidth: 60 }}
          >
            Stg
          </Button>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "25%",
      render: (text) => (
        <Typography.Text code>{text}</Typography.Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "45%",
      render: (text) => text || "No description",
    },
    {
      title: "Versions",
      key: "versions",
      width: "30%",
      render: (_, record) => getVersionButtons(record.id, record.versions),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Search criteria row */}
          <Space style={{ marginBottom: 16, width: "100%" }} wrap>
            <AutoComplete
              mode="multiple"
              style={{ minWidth: 250 }}
              options={aspects.capabilities.map(cap => ({ value: cap }))}
              value={capabilitySearch}
              onChange={(value) => setCapabilitySearch(value)}
                  placeholder="Select capabilities..."
                />
            <AutoComplete
              mode="multiple"
              style={{ minWidth: 250 }}
              options={aspects.functions.map(func => ({ value: func }))}
              value={functionSearch}
              onChange={(value) => setFunctionSearch(value)}
                  placeholder="Select functions..."
            />
            <Button
              type="default"
              onClick={handleSearch}
              disabled={
                capabilitySearch.length === 0 && functionSearch.length === 0
              }
            >
              Search
            </Button>
            {isTargetSearch && (
              <Button onClick={handleClearSearch}>
                Clear
              </Button>
            )}
          </Space>

          {/* Filter and action buttons row */}
          <Space style={{ marginBottom: 16, width: "100%" }} wrap>
              <Select
              style={{ minWidth: 120, height: "32px" }}
                value={envFilter}
              onChange={(value) => setEnvFilter(value)}
              >
              <Option value="both">Any</Option>
              <Option value="prod">Prod</Option>
              <Option value="stg">Stg</Option>
              </Select>
            <Button onClick={handleReload} style={{ height: "32px" }}>
              Reload
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setSelectedPremade({
                  premadeId: null,
                  version: "draft",
                });
                setScriptDialogOpen(true);
              }}
              style={{ height: "32px" }}
            >
              Add
            </Button>
            <Input
              style={{ flex: 1, maxWidth: 400, height: "32px" }}
                placeholder="Filter by ID or description..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
          </Space>

          <Card>
            <Table
              columns={columns}
              dataSource={Array.isArray(filteredPremades) ? filteredPremades : []}
              rowKey="id"
              pagination={false}
            />
          </Card>

          {filteredPremades.length === 0 && !loading && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <Typography.Text type="secondary">
                {searchTerm
                  ? "No premades match your search"
                  : "No premades found"}
              </Typography.Text>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary">
              {isTargetSearch ? (
                <>
                  Search results: {filteredPremades.length} premade
                  {filteredPremades.length !== 1 ? "s" : ""}
                </>
              ) : (
                <>
                  Total: {filteredPremades.length} premade
                  {filteredPremades.length !== 1 ? "s" : ""}
                </>
              )}
            </Typography.Text>
          </div>
        </>
      )}

      <PremadeScriptDialog
        open={scriptDialogOpen}
        onClose={() => {
          setScriptDialogOpen(false);
          setSelectedPremade(null);
          // Refresh the data to show any changes
          fetchData(isTargetSearch);
        }}
        premadeId={selectedPremade?.premadeId}
        version={selectedPremade?.version}
      />
    </div>
  );
};

export default PremadeManageView;
